import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { CrmObjectService } from './crm-object-service/crm-object.service';
import {
  GalaxyFilterDefinitionInterface,
  GalaxyFilterType,
  GalaxyFilterOperator,
  GalaxyFilterInterface,
} from '@vendasta/galaxy/filter/chips';
import { TaskTableCustomizationService } from '../table-customization/task-table-customization.service';
import { ContactTableCustomizationService } from '../table-customization/contact-table-customization.service';
import { CompanyTableCustomizationService } from '../table-customization/company-table-customization.service';
import { CrmInjectionToken, ActivityType, CrmDependencies, ObjectType } from '../../tokens-and-interfaces';
import { FilterOperator, FilterType } from '@vendasta/crm';

// right now filters are sorted manually in the backend, the page size needs to cover the amount of system + standard + extensions + custom filters (up to 100)
const FILTER_PAGE_SIZE = 200;

function convertToGalaxyFilterType(type: FilterType): GalaxyFilterType {
  return GalaxyFilterType[FilterType[type]];
}
function convertToGalaxyFilterOperator(operator: FilterOperator): GalaxyFilterOperator {
  return GalaxyFilterOperator[FilterOperator[operator]];
}
function convertToGalaxyFilterOperators(operators: FilterOperator[]): GalaxyFilterOperator[] {
  return operators.map((o) => convertToGalaxyFilterOperator(o));
}

@Injectable()
export class CrmFiltersService {
  private initialFilters$$ = new BehaviorSubject<GalaxyFilterInterface[]>([]);
  initialFilters$ = this.initialFilters$$.asObservable();

  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private readonly crmObjectService: CrmObjectService,
    private readonly contactTableCustomization: ContactTableCustomizationService,
    private readonly companyTableCustomization: CompanyTableCustomizationService,
    private readonly taskTableCustomization: TaskTableCustomizationService,
  ) {}

  private customizeFilters(
    filters: GalaxyFilterDefinitionInterface[],
    objectType: ObjectType,
    activityType?: ActivityType,
  ): Observable<GalaxyFilterDefinitionInterface[]> {
    switch (objectType) {
      case 'Contact':
        return this.contactTableCustomization.customizeFilters(filters);
      case 'Company':
        return this.companyTableCustomization.customizeFilters(filters);
      case 'Activity':
        switch (activityType) {
          case 'Task':
            return this.taskTableCustomization.customizeFilters(filters);
          default:
            return of(filters);
        }
      default:
        return of(filters);
    }
  }

  listObjectFilters$(
    searchTerm: string,
    objectType: ObjectType,
    activityType?: ActivityType,
  ): Observable<GalaxyFilterDefinitionInterface[]> {
    return this.config.namespace$.pipe(
      switchMap((namespace) => {
        return this.crmObjectService.listObjectsFilters(objectType, {
          namespace: namespace,
          paging: {
            pageSize: FILTER_PAGE_SIZE,
          },
          searchTerm: searchTerm,
        });
      }),
      map((resp) => {
        if (!resp.filters) {
          return [] as GalaxyFilterDefinitionInterface[];
        }
        return resp.filters.map((filterDef) => ({
          fieldId: filterDef.fieldId,
          type: convertToGalaxyFilterType(filterDef.typeV2 || FilterType.FILTER_TYPE_INVALID),
          hasFilterOptions: filterDef.hasFilterOptions,
          supportedOperators: convertToGalaxyFilterOperators(filterDef.supportedOperatorsV2 || []),
          fieldName: filterDef.fieldName,
        }));
      }),
      switchMap((filters) => this.customizeFilters(filters, objectType, activityType)),
    );
  }

  async getInitialAppliedFilters$(
    objectType: ObjectType,
    activityType?: ActivityType,
    useUrlFilters?: boolean,
  ): Promise<GalaxyFilterInterface[]> {
    if (objectType === 'Activity' && activityType === 'Task') {
      const urlFilters = firstValueFrom(this.initialFilters$);
      return urlFilters.then((filters) => {
        if (filters?.length > 0 && useUrlFilters) return filters;
        return this.taskTableCustomization.getInitialFilters();
      });
    }
    if (objectType === 'Contact') {
      const urlFilters = await firstValueFrom(this.initialFilters$);
      const initialContactFilters = await this.contactTableCustomization.getInitialFilters();
      if (urlFilters?.length > 0 && useUrlFilters) {
        const joinedFilters = [...urlFilters, ...initialContactFilters];
        return joinedFilters.filter((filter, index) => {
          return index === joinedFilters.findIndex((f) => filter.filterId === f.filterId);
        });
      }
      return initialContactFilters;
    }
    if (objectType === 'Company') {
      const urlFilters = await firstValueFrom(this.initialFilters$);
      const initialCompanyFilters = await this.companyTableCustomization.getInitialFilters();
      if (urlFilters?.length > 0 && useUrlFilters) {
        const joinedFilters = [...urlFilters, ...initialCompanyFilters];
        return joinedFilters.filter((filter, index) => {
          return index === joinedFilters.findIndex((f) => filter.filterId === f.filterId);
        });
      }
      return initialCompanyFilters;
    }
    return firstValueFrom(this.initialFilters$);
  }

  setInitialFilters(filters: GalaxyFilterInterface[]) {
    this.initialFilters$$.next(filters);
  }
}
